import React from 'react';

import {
  AppIcon,
  ColorToken,
  DonutChart,
  DonutSegment,
  useContainerQuery,
} from '@backrs_ui/common-ui';

import { ProgressBarChart } from '@backrs_ui/common-ui/src/atoms/Charts/ProgressBarChart';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  GUIDE_TEXT_MAX_WIDTH,
  GuideAccordionList,
  SectionHeader,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import type {
  InternshipGuideConclusion,
  InternshipGuideSixStepProcess,
} from '@backrs_ui/grow-ui/src/pages/InternshipGuide/content/types';

import { Box, Divider, SxProps, Theme, Typography } from '@mui/material';

type SixStepProcessSectionProps = Readonly<{
  content: InternshipGuideSixStepProcess;
  conclusion: InternshipGuideConclusion;
}>;

const timeAllocationStrings = appStrings.modules.internshipGuide.timeAllocation;
const callbackRateStrings = appStrings.modules.internshipGuide.interviewCallbackRate;

const TIME_ALLOCATION_SEGMENTS: DonutSegment[] = [
  { label: timeAllocationStrings.networking, value: 35, color: 'Blue/500' },
  { label: timeAllocationStrings.research, value: 25, color: 'Green/500' },
  { label: timeAllocationStrings.applications, value: 20, color: 'Colors/Base/Black' },
  { label: timeAllocationStrings.prepAndPractice, value: 15, color: 'Yellow/500' },
  { label: timeAllocationStrings.followUps, value: 5, color: 'BlueDark/900' },
];

const CALLBACK_RATE_ITEMS: { label: string; value: number; color: ColorToken }[] = [
  { label: callbackRateStrings.withReferral, value: 43, color: 'Brand/900' },
  { label: callbackRateStrings.tailoredApp, value: 25, color: 'Brand/700' },
  { label: callbackRateStrings.coldApply, value: 8, color: 'Brand/500' },
  { label: callbackRateStrings.massApply, value: 1.5, color: 'Brand/300' },
];
const CALLBACK_RATE_MAX = 50;

export const SixStepProcessSection: React.FC<SixStepProcessSectionProps> = ({
  content,
  conclusion,
}) => {
  return (
    <Box sx={containerSx}>
      <Box sx={introRowSx}>
        <Box sx={paragraphColumnSx}>
          {content.paragraphs.map((paragraph) => (
            <Typography key={paragraph} variant="text-md-regular">
              {paragraph}
            </Typography>
          ))}
        </Box>
        <TimeAllocationCard />
      </Box>

      <Box sx={accordionListSx}>
        <GuideAccordionList
          idPrefix="six-step-accordion"
          detailsSx={detailsSx}
          items={content.steps.map((step) => ({
            id: step.id,
            title: `${step.number}. ${step.title}`,
            details: (
              <Box sx={detailsGridSx}>
                <Box sx={mainColumnSx}>
                  <Typography variant="text-md-regular" sx={bodyTextSx}>
                    {step.intro}
                  </Typography>
                  {step.subsections.map((subsection) => (
                    <Box key={subsection.heading} sx={subsectionSx}>
                      <Typography variant="text-md-semibold" sx={subsectionHeadingSx}>
                        {subsection.heading}
                      </Typography>
                      <Typography variant="text-md-regular" sx={bodyTextSx}>
                        {subsection.content}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {step.bottomLine && (
                  <Box sx={bottomLineSx}>
                    <Typography variant="text-lg-semibold">{content.bottomLineLabel}</Typography>
                    <Typography variant="text-md-regular" sx={bodyTextSx}>
                      {step.bottomLine}
                    </Typography>
                  </Box>
                )}
              </Box>
            ),
          }))}
        />
      </Box>

      <Divider />
      <Box sx={conclusionHeaderSx}>
        <SectionHeader title={conclusion.title} subtitle={conclusion.subtitle} hideDivider />
      </Box>
      <Box sx={outroRowSx}>
        <Box sx={paragraphColumnSx}>
          {conclusion.paragraphs.map((paragraph) => (
            <Typography key={paragraph} variant="text-md-regular">
              {paragraph}
            </Typography>
          ))}
        </Box>
        <InterviewCallbackCard />
      </Box>
    </Box>
  );
};

const TimeAllocationCard: React.FC = () => {
  const [containerRef, isCompact] = useContainerQuery(600);

  return (
    <Box ref={containerRef} sx={chartCardContainerSx}>
      <Box sx={cardHeaderSx}>
        <Typography variant="text-sm-semibold" sx={chartTitleSx}>
          {timeAllocationStrings.title}
        </Typography>
      </Box>
      <Box sx={cardBodySx}>
        <DonutChart
          variant={{ type: 'multi', segments: TIME_ALLOCATION_SEGMENTS }}
          size={isCompact ? 'small' : 200}
        />
        <Box sx={legendContainerSx}>
          {TIME_ALLOCATION_SEGMENTS.map((segment) => (
            <Box key={segment.label} display="flex" flexDirection="column">
              <Box sx={legendItemSx}>
                <AppIcon
                  iconName="Circle"
                  diameter="10px"
                  color="Colors/Base/Black"
                  fill={segment.color}
                />
                <Typography variant="text-sm-medium" sx={legendLabelSx}>
                  {segment.label}
                </Typography>
              </Box>
              <Typography variant="text-xs-semibold" sx={legendPercentSx}>
                {`${segment.value}%`}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const InterviewCallbackCard: React.FC = () => {
  return (
    <Box sx={chartCardContainerSx}>
      <Box sx={cardHeaderSx}>
        <Typography variant="text-sm-semibold" sx={chartTitleSx}>
          {callbackRateStrings.title}
        </Typography>
      </Box>
      <Box sx={barCardBodySx}>
        {CALLBACK_RATE_ITEMS.map((item) => (
          <Box key={item.label} sx={barRowSx}>
            <Typography variant="text-sm-medium" sx={barLabelSx}>
              {item.label}
            </Typography>
            <ProgressBarChart value={item.value} max={CALLBACK_RATE_MAX} color={item.color} />
            <Typography variant="text-sm-semibold" sx={barValueSx}>
              {`${item.value}%`}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const introRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'start',
  mb: backrs.spacing['spacing-3xl'],
  containerType: 'inline-size',
  '@container (min-width: 768px)': {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 380px)',
  },
});

const paragraphColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  color: backrs.colors['Colors/Text/text-tertiary'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const conclusionHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-3xl'],
});

const outroRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'start',
  mt: backrs.spacing['spacing-3xl'],
  containerType: 'inline-size',
  '@container (min-width: 768px)': {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 380px)',
  },
});

const barCardBodySx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  py: backrs.spacing['spacing-2xl'],
  px: backrs.spacing['spacing-lg'],
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const barRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const barLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  width: '96px',
  flexShrink: 0,
});

const barValueSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  flexShrink: 0,
});

const chartCardContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  maxWidth: '380px',
});

const cardHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  py: backrs.spacing['spacing-lg'],
  px: backrs.spacing['spacing-lg'],
});

const chartTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const cardBodySx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  py: backrs.spacing['spacing-2xl'],
  px: backrs.spacing['spacing-lg'],
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'flex-start',
  justifyItems: 'center',
  '@container (min-width: 400px)': {
    gridTemplateColumns: '1fr 1fr',
    gap: backrs.spacing['spacing-6xl'],
    justifyItems: 'start',
  },
});

const legendContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const legendItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const legendLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const legendPercentSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  paddingLeft: '18px',
});

const accordionListSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const detailsSx: SxProps<Theme> = {
  containerType: 'inline-size',
};

const detailsGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-xl'],
  alignItems: 'start',
  '@container (min-width: 600px)': {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 280px)',
  },
});

const mainColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const bodyTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const subsectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const subsectionHeadingSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const bottomLineSx: SxProps<Theme> = ({ backrs }) => ({
  p: backrs.spacing['spacing-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-md'],
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});
